import { useState, useCallback } from 'react';
import { LogEntry, User } from '../types';
import { addLog as dbAddLog, loadLogs, loadUsers } from '../services/rhDataService';

export interface UseAuditLogParams {
  currentUser: User | null;
  currentCompanyId: string | null;
  getSB: () => ReturnType<typeof import('../services/postgresClient').getPostgresClient>;
  getVerifiedDate: () => Date;
}

/**
 * Logs de auditoria e lista de usuários — exige perm_settings para carregar do banco.
 * @param params - Usuário logado e acesso ao Postgres
 * @returns Estado de logs/usuários e função addLog
 */
export function useAuditLog({ currentUser, currentCompanyId, getSB, getVerifiedDate }: UseAuditLogParams) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const addLog = useCallback(
    (action: string, details: string) => {
      if (!currentUser || !currentCompanyId) return;
      const newLog: LogEntry = {
        id: Date.now().toString(),
        userId: currentUser.id,
        username: currentUser.username,
        timestamp: getVerifiedDate().toISOString(),
        action,
        details,
        companyId: currentCompanyId,
      };
      setLogs((prev) => [newLog, ...prev].slice(0, 1000));
      const sb = getSB();
      if (sb) dbAddLog(sb, newLog, currentCompanyId).catch(console.warn);
    },
    [currentUser, currentCompanyId, getVerifiedDate, getSB],
  );

  const resetAuditState = useCallback(() => {
    setLogs([]);
    setUsers([]);
  }, []);

  /** Carrega logs e usuários do banco após login (não bloqueia em falha). */
  const loadAuditDataFromDb = useCallback(
    async (sb: ReturnType<typeof getSB>, companyId: string) => {
      try {
        const dbLogs = await loadLogs(sb, companyId);
        setLogs(dbLogs);
      } catch (err) {
        console.warn('[Postgres] Falha ao listar logs (não bloqueia login)', err);
        setLogs([]);
      }

      try {
        const sbUsers = await loadUsers(sb);
        if (sbUsers.length > 0) setUsers(sbUsers);
      } catch (err) {
        console.warn('[Postgres] Falha ao listar usuários (não bloqueia login)', err);
      }
    },
    [],
  );

  return {
    logs,
    users,
    setUsers,
    addLog,
    resetAuditState,
    loadAuditDataFromDb,
    setLogs,
  };
}
